import { createReducer, on, Action } from '@ngrx/store';
import { parseSKonAggregateDailies, RealTimeTelemetryService, normalizeRTTimestamp } from 'ngx-core-services';

import {
  requestPlantCardBlocksToShow,
  requestPlantCardStatus,
  requestSelectedHeatpumpCurrentData,
  requestSelectedHeatpumpDailyTelemetryData,
  requestSystemAlerts,
  requestSystemAlertsCount,
  requestSystemCurrentEvents,
  requestSystemDailyEnergies,
  requestSystemDailyPowers,
  requestSystemDeviceDailyPowers,
  requestSystemDeviceDetails,
  requestSystemEnergyDataOverview,
  requestSystemEnergyState,
  requestSystemEvchargeDailyTelemetry,
  requestSystemEvchargeEnergyAggregation,
  requestSystemHeatpumpEnergyAggregation,
  requestSystemHistoricalEvents,
  requestSystemMonthlyYearlyEnergy,
  requestSystemPowerState,
  requestSystemsDevicesStatus,
  setPlantCardBlocksToShowError,
  setPlantCardStatusError,
  setSelectedHeatpumpCurrentDataError,
  setSelectedHeatpumpDailyTelemetryDataError,
  setSystemAlertsError,
  setSystemCurrentEventError,
  setSystemDeviceDailyPowersError,
  setSystemDeviceDetailsError,
  setSystemEnergyDataOverviewError,
  setSystemEnergyStateError,
  setSystemEvchargeDailyTelemetryError,
  setSystemEvchargeEnergyAggregationError,
  setSystemHeatpumpEnergyAggregationError,
  setSystemHistoricalEventError,
  setSystemMonthlyYearlyEnergyError,
  setSystemPowerStateError,
  updatePlantCardBlocksToShow,
  updatePlantCardStatus,
  updateSelectedHeatpumpCurrentData,
  updateSelectedHeatpumpDailyTelemetryData,
  updateSystemAlerts,
  updateSystemAlertsCount,
  updateSystemCurrentEvents,
  updateSystemDailyEnergies,
  updateSystemDailyPowers,
  updateSystemDeviceDailyPowers,
  updateSystemDeviceDetails,
  updateSystemEnergyDataOverview,
  updateSystemEnergyState,
  updateSystemEvchargeDailyTelemetry,
  updateSystemEvchargeEnergyAggregation,
  updateSystemHeatpumpEnergyAggregation,
  updateSystemHistoricalEvents,
  updateSystemMonthlyYearlyEnergy,
  updateSystemPowerState,
  updateSystemsDevicesStatus,
} from '../actions/block.actions';
import {
  deactivateRealTimeTelemetry,
  realTimeTelemetryActivated,
  realTimeTelemetryActivationFailed,
  realTimeTelemetryDataReceived,
  realTimeTelemetryDeactivationFailed,
  realTimeTelemetrySessionEnded,
  setRealTimeTelemetryOverlayHidden,
} from '../actions/real-time.actions';
import { BlockState, initialBlockState } from '../state/block.state';
import { BuildPowerFlow } from '../effects/effects.utils';

const _blockReducer = createReducer(
  initialBlockState,
  on(requestSystemAlerts, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      AlertsListComponent: {
        ...state.blockList.AlertsListComponent,
        [instanceId]: {
          ...state.blockList.AlertsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.AlertsListComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemAlerts, (state, { systemId, instanceId, alerts, allTimestampsBySerial }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      AlertsListComponent: {
        ...state.blockList.AlertsListComponent,
        [instanceId]: {
          ...state.blockList.AlertsListComponent[instanceId],
          [systemId]: {
            alerts,
            allTimestampsBySerial,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemAlertsError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      AlertsListComponent: {
        ...state.blockList.AlertsListComponent,
        [instanceId]: {
          ...state.blockList.AlertsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.AlertsListComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemAlertsCount, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      AlertsCounterComponent: {
        ...state.blockList.AlertsCounterComponent,
        [instanceId]: {
          ...state.blockList.AlertsCounterComponent[instanceId],
          [systemId]: {
            ...state.blockList.AlertsCounterComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemAlertsCount, (state, { systemId, instanceId, alerts }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      AlertsCounterComponent: {
        ...state.blockList.AlertsCounterComponent,
        [instanceId]: {
          ...state.blockList.AlertsCounterComponent[instanceId],
          [systemId]: {
            alerts,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemAlertsError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      AlertsCounterComponent: {
        ...state.blockList.AlertsCounterComponent,
        [instanceId]: {
          ...state.blockList.AlertsCounterComponent[instanceId],
          [systemId]: {
            ...state.blockList.AlertsCounterComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemDailyEnergies, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyStateOverviewComponent: {
        ...state.blockList.DailyStateOverviewComponent,
        [instanceId]: {
          ...state.blockList.DailyStateOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyStateOverviewComponent[instanceId]?.[systemId],
            energyRequestState: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemDailyEnergies, (state, { systemId, instanceId, systemEnergyState }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyStateOverviewComponent: {
        ...state.blockList.DailyStateOverviewComponent,
        [instanceId]: {
          ...state.blockList.DailyStateOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyStateOverviewComponent[instanceId]?.[systemId],
            systemEnergyState,
            energyRequestState: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemEnergyStateError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyStateOverviewComponent: {
        ...state.blockList.DailyStateOverviewComponent,
        [instanceId]: {
          ...state.blockList.DailyStateOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyStateOverviewComponent[instanceId]?.[systemId],
            energyRequestState: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemDailyPowers, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyStateOverviewComponent: {
        ...state.blockList.DailyStateOverviewComponent,
        [instanceId]: {
          ...state.blockList.DailyStateOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyStateOverviewComponent[instanceId]?.[systemId],
            powerRequestState: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemDailyPowers, (state, { systemId, instanceId, systemPowerState }) => {
    const existing = state.blockList.DailyStateOverviewComponent?.[instanceId]?.[systemId];
    const rtPowerPoints = existing?.rtPowerPoints ?? [];
    return {
      ...state,
      blockList: {
        ...state.blockList,
        DailyStateOverviewComponent: {
          ...state.blockList.DailyStateOverviewComponent,
          [instanceId]: {
            ...state.blockList.DailyStateOverviewComponent[instanceId],
            [systemId]: {
              ...existing,
              systemPowerState: rtPowerPoints.length > 0
                ? [...systemPowerState, ...rtPowerPoints]
                : systemPowerState,
              powerRequestState: 'ready' as const,
            }
          }
        }
      }
    };
  }),
  on(setSystemPowerStateError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyStateOverviewComponent: {
        ...state.blockList.DailyStateOverviewComponent,
        [instanceId]: {
          ...state.blockList.DailyStateOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyStateOverviewComponent[instanceId]?.[systemId],
            powerRequestState: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemDeviceDetails, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DeviceDetailsComponent: {
        ...state.blockList.DeviceDetailsComponent,
        [instanceId]: {
          ...state.blockList.DeviceDetailsComponent[instanceId],
          [systemId]: {
            ...state.blockList.DeviceDetailsComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemDeviceDetails, (state, { systemId, instanceId, deviceDetails, deviceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DeviceDetailsComponent: {
        ...state.blockList.DeviceDetailsComponent,
        [instanceId]: {
          ...state.blockList.DeviceDetailsComponent[instanceId],
          [systemId]: {
            details: deviceDetails,
            deviceId,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemDeviceDetailsError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DeviceDetailsComponent: {
        ...state.blockList.DeviceDetailsComponent,
        [instanceId]: {
          ...state.blockList.DeviceDetailsComponent[instanceId],
          [systemId]: {
            ...state.blockList.DeviceDetailsComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemEnergyDataOverview, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EnergyDataOverviewComponent: {
        ...state.blockList.EnergyDataOverviewComponent,
        [instanceId]: {
          ...state.blockList.EnergyDataOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.EnergyDataOverviewComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemEnergyDataOverview, (state, { systemId, instanceId, systemEnergyState, type, mode, range }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EnergyDataOverviewComponent: {
        ...state.blockList.EnergyDataOverviewComponent,
        [instanceId]: {
          ...state.blockList.EnergyDataOverviewComponent[instanceId],
          [systemId]: {
            systemEnergyState,
            type,
            mode,
            range,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemEnergyDataOverviewError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EnergyDataOverviewComponent: {
        ...state.blockList.EnergyDataOverviewComponent,
        [instanceId]: {
          ...state.blockList.EnergyDataOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.EnergyDataOverviewComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemEnergyState, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      SystemTotalizerComponent: {
        ...state.blockList.SystemTotalizerComponent,
        [instanceId]: {
          ...state.blockList.SystemTotalizerComponent[instanceId],
          [systemId]: {
            ...state.blockList.SystemTotalizerComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemEnergyState, (state, { systemId, instanceId, systemEnergyState }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      SystemTotalizerComponent: {
        ...state.blockList.SystemTotalizerComponent,
        [instanceId]: {
          ...state.blockList.SystemTotalizerComponent[instanceId],
          [systemId]: {
            systemEnergyState,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemEnergyStateError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      SystemTotalizerComponent: {
        ...state.blockList.SystemTotalizerComponent,
        [instanceId]: {
          ...state.blockList.SystemTotalizerComponent[instanceId],
          [systemId]: {
            ...state.blockList.SystemTotalizerComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemMonthlyYearlyEnergy, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      MonthlyYearlyEnergyOverviewComponent: {
        ...state.blockList.MonthlyYearlyEnergyOverviewComponent,
        [instanceId]: {
          ...state.blockList.MonthlyYearlyEnergyOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.MonthlyYearlyEnergyOverviewComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemMonthlyYearlyEnergy, (state, { systemId, instanceId, systemEnergyState, mode, range }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      MonthlyYearlyEnergyOverviewComponent: {
        ...state.blockList.MonthlyYearlyEnergyOverviewComponent,
        [instanceId]: {
          ...state.blockList.MonthlyYearlyEnergyOverviewComponent[instanceId],
          [systemId]: {
            systemEnergyState,
            mode,
            range,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemMonthlyYearlyEnergyError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      MonthlyYearlyEnergyOverviewComponent: {
        ...state.blockList.MonthlyYearlyEnergyOverviewComponent,
        [instanceId]: {
          ...state.blockList.MonthlyYearlyEnergyOverviewComponent[instanceId],
          [systemId]: {
            ...state.blockList.MonthlyYearlyEnergyOverviewComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemPowerState, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      FlowChartComponent: {
        ...state.blockList.FlowChartComponent,
        [instanceId]: {
          ...state.blockList.FlowChartComponent[instanceId],
          [systemId]: {
            ...state.blockList.FlowChartComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemPowerState, (state, { systemId, instanceId, systemPowerState }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      FlowChartComponent: {
        ...state.blockList.FlowChartComponent,
        [instanceId]: {
          ...state.blockList.FlowChartComponent[instanceId],
          [systemId]: {
            systemPowerState,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemPowerStateError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      FlowChartComponent: {
        ...state.blockList.FlowChartComponent,
        [instanceId]: {
          ...state.blockList.FlowChartComponent[instanceId],
          [systemId]: {
            ...state.blockList.FlowChartComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemsDevicesStatus, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DeviceListComponent: {
        ...state.blockList.DeviceListComponent,
        [instanceId]: {
          ...state.blockList.DeviceListComponent[instanceId],
          [systemId]: {
            ...state.blockList.DeviceListComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemsDevicesStatus, (state, { systemId, instanceId, devices }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DeviceListComponent: {
        ...state.blockList.DeviceListComponent,
        [instanceId]: {
          ...state.blockList.DeviceListComponent[instanceId],
          [systemId]: {
            devices,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemPowerStateError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DeviceListComponent: {
        ...state.blockList.DeviceListComponent,
        [instanceId]: {
          ...state.blockList.DeviceListComponent[instanceId],
          [systemId]: {
            ...state.blockList.DeviceListComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),


  on(requestSystemDeviceDailyPowers, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyDevicePowersStateComponent: {
        ...state.blockList.DailyDevicePowersStateComponent,
        [instanceId]: {
          ...state.blockList.DailyDevicePowersStateComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyDevicePowersStateComponent[instanceId]?.[systemId],
            powerRequestState: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemDeviceDailyPowers, (state, { systemId, instanceId, systemPowerState, range }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyDevicePowersStateComponent: {
        ...state.blockList.DailyDevicePowersStateComponent,
        [instanceId]: {
          ...state.blockList.DailyDevicePowersStateComponent[instanceId],
          [systemId]: {
            systemPowerState,
            range,
            powerRequestState: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemDeviceDailyPowersError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      DailyDevicePowersStateComponent: {
        ...state.blockList.DailyDevicePowersStateComponent,
        [instanceId]: {
          ...state.blockList.DailyDevicePowersStateComponent[instanceId],
          [systemId]: {
            ...state.blockList.DailyDevicePowersStateComponent[instanceId]?.[systemId],
            powerRequestState: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemCurrentEvents, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EventsListComponent: {
        ...state.blockList.EventsListComponent,
        [instanceId]: {
          ...state.blockList.EventsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.EventsListComponent[instanceId]?.[systemId],
            currentEvent: {
              state: 'pending' as const,
            }
          }
        }
      }
    }
  })),
  on(requestSystemHistoricalEvents, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EventsListComponent: {
        ...state.blockList.EventsListComponent,
        [instanceId]: {
          ...state.blockList.EventsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.EventsListComponent[instanceId]?.[systemId],
            eventsHistory: {
              state: 'pending' as const,
            }
          }
        }
      }
    }
  })),
  on(updateSystemCurrentEvents, (state, { systemId, instanceId, events }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EventsListComponent: {
        ...state.blockList.EventsListComponent,
        [instanceId]: {
          ...state.blockList.EventsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.EventsListComponent[instanceId]?.[systemId],
            currentEvent: {
              events,
              state: 'ready' as const,
            }
          }
        }
      }
    }
  })),
  on(updateSystemHistoricalEvents, (state, { systemId, instanceId, events }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EventsListComponent: {
        ...state.blockList.EventsListComponent,
        [instanceId]: {
          ...state.blockList.EventsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.EventsListComponent[instanceId]?.[systemId],
            eventsHistory: {
              events,
              state: 'ready' as const,
            }
          }
        }
      }
    }
  })),
  on(setSystemCurrentEventError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EventsListComponent: {
        ...state.blockList.EventsListComponent,
        [instanceId]: {
          ...state.blockList.EventsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.EventsListComponent[instanceId]?.[systemId],
            currentEvent: {
              ...state.blockList.EventsListComponent[instanceId]?.[systemId]?.currentEvent,
              state: 'error' as const,
              error
            }
          }
        }
      }
    }
  })),
  on(setSystemHistoricalEventError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EventsListComponent: {
        ...state.blockList.EventsListComponent,
        [instanceId]: {
          ...state.blockList.EventsListComponent[instanceId],
          [systemId]: {
            ...state.blockList.EventsListComponent[instanceId]?.[systemId],
            eventsHistory: {
              ...state.blockList.EventsListComponent[instanceId]?.[systemId]?.eventsHistory,
              state: 'error' as const,
              error
            }
          }
        }
      }
    }
  })),
  on(requestPlantCardBlocksToShow, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      PlantCardComponent: {
        ...state.blockList.PlantCardComponent,
        [systemId]: {
          ...state.blockList.PlantCardComponent?.[systemId],
          state: 'pending' as const,
        }
      }
    }
  })),
  on(updatePlantCardBlocksToShow, (state, { systemId, blocks: blocksToShow }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      PlantCardComponent: {
        ...state.blockList.PlantCardComponent,
        [systemId]: {
          ...state.blockList.PlantCardComponent?.[systemId],
          blocksToShow,
          timestamp: Date.now(),
          state: 'ready' as const,
        },
      }
    }
  })),
  on(setPlantCardBlocksToShowError, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      PlantCardComponent: {
        ...state.blockList.PlantCardComponent,
        [systemId]: {
          ...state.blockList.PlantCardComponent?.[systemId],
          state: 'error' as const,
        },
      }
    }
  })),
  on(requestPlantCardStatus, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      PlantCardComponent: {
        ...state.blockList.PlantCardComponent,
        [systemId]: {
          ...state.blockList.PlantCardComponent?.[systemId],
          state: 'pending' as const,
        }
      }
    }
  })),
  on(updatePlantCardStatus, (state, { systemId, plantStatus, hasAlarms }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      PlantCardComponent: {
        ...state.blockList.PlantCardComponent,
        [systemId]: {
          ...state.blockList.PlantCardComponent?.[systemId],
          plantStatus,
          hasAlarms,
          state: 'ready' as const,
          timestamp: Date.now(),
        }
      }
    }
  })),
  on(setPlantCardStatusError, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      PlantCardComponent: {
        ...state.blockList.PlantCardComponent,
        [systemId]: {
          ...state.blockList.PlantCardComponent?.[systemId],
          state: 'error' as const,
        }
      }
    }
  })),
  on(requestSystemEvchargeDailyTelemetry, (state, { systemId, instanceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EvchargeTelemetryComponent: {
        ...state.blockList.EvchargeTelemetryComponent,
        [instanceId]: {
          ...state.blockList.EvchargeTelemetryComponent[instanceId],
          [systemId]: {
            ...state.blockList.EvchargeTelemetryComponent[instanceId]?.[systemId],
            state: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemEvchargeDailyTelemetry, (state, { systemId, instanceId, telemetryData, range, deviceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EvchargeTelemetryComponent: {
        ...state.blockList.EvchargeTelemetryComponent,
        [instanceId]: {
          ...state.blockList.EvchargeTelemetryComponent[instanceId],
          [systemId]: {
            telemetryData,
            range,
            deviceId,
            state: 'ready' as const,
          }
        }
      }
    }
  })),
  on(setSystemEvchargeDailyTelemetryError, (state, { systemId, instanceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EvchargeTelemetryComponent: {
        ...state.blockList.EvchargeTelemetryComponent,
        [instanceId]: {
          ...state.blockList.EvchargeTelemetryComponent[instanceId],
          [systemId]: {
            ...state.blockList.EvchargeTelemetryComponent[instanceId]?.[systemId],
            state: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSystemEvchargeEnergyAggregation, (state, { systemId, instanceId, period }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EvchargeEnergyAggregationComponent: {
        ...state.blockList.EvchargeEnergyAggregationComponent,
        [instanceId]: {
          ...state.blockList.EvchargeEnergyAggregationComponent[instanceId],
          [systemId]: {
            ...state.blockList.EvchargeEnergyAggregationComponent[instanceId]?.[systemId],
            [period === 'month' ? 'monthlyState' : 'yearlyState']: 'pending' as const,
          }
        }
      }
    }
  })),
  on(updateSystemEvchargeEnergyAggregation, (state, { systemId, instanceId, period, aggregationData, deviceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EvchargeEnergyAggregationComponent: {
        ...state.blockList.EvchargeEnergyAggregationComponent,
        [instanceId]: {
          ...state.blockList.EvchargeEnergyAggregationComponent[instanceId],
          [systemId]: {
            ...state.blockList.EvchargeEnergyAggregationComponent[instanceId]?.[systemId],
            [period === 'month' ? 'monthlyData' : 'yearlyData']: aggregationData,
            [period === 'month' ? 'monthlyState' : 'yearlyState']: 'ready' as const,
            deviceId,
          }
        }
      }
    }
  })),
  on(setSystemEvchargeEnergyAggregationError, (state, { systemId, instanceId, period, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      EvchargeEnergyAggregationComponent: {
        ...state.blockList.EvchargeEnergyAggregationComponent,
        [instanceId]: {
          ...state.blockList.EvchargeEnergyAggregationComponent[instanceId],
          [systemId]: {
            ...state.blockList.EvchargeEnergyAggregationComponent[instanceId]?.[systemId],
            [period === 'month' ? 'monthlyState' : 'yearlyState']: 'error' as const,
            error
          }
        }
      }
    }
  })),
  on(requestSelectedHeatpumpCurrentData, (state, { systemId, instanceId, deviceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpFlowChartComponent: {
        ...state.blockList.HeatpumpFlowChartComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpFlowChartComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpFlowChartComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpFlowChartComponent[instanceId]?.[systemId]?.[deviceId],
              state: 'pending' as const,
            }
          }
        }
      }
    }
  })),
  on(updateSelectedHeatpumpCurrentData, (state, { systemId, instanceId, deviceId, heatpumpData }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpFlowChartComponent: {
        ...state.blockList.HeatpumpFlowChartComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpFlowChartComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpFlowChartComponent[instanceId]?.[systemId],
            [deviceId]: {
              heatpumpData,
              state: 'ready' as const,
            }
          }
        }
      }
    }
  })),
  on(setSelectedHeatpumpCurrentDataError, (state, { systemId, instanceId, deviceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpFlowChartComponent: {
        ...state.blockList.HeatpumpFlowChartComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpFlowChartComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpFlowChartComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpFlowChartComponent[instanceId]?.[systemId]?.[deviceId],
              state: 'error' as const,
              error,
            }
          }
        }
      }
    }
  })),
  on(requestSelectedHeatpumpDailyTelemetryData, (state, { systemId, instanceId, deviceId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpDailyTelemetryComponent: {
        ...state.blockList.HeatpumpDailyTelemetryComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId]?.[systemId]?.[deviceId],
              state: 'pending' as const,
            }
          }
        }
      }
    }
  })),
  on(updateSelectedHeatpumpDailyTelemetryData, (state, { systemId, instanceId, deviceId, heatpumpData }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpDailyTelemetryComponent: {
        ...state.blockList.HeatpumpDailyTelemetryComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId]?.[systemId],
            [deviceId]: {
              heatpumpData,
              state: 'ready' as const,
            }
          }
        }
      }
    }
  })),
  on(setSelectedHeatpumpDailyTelemetryDataError, (state, { systemId, instanceId, deviceId, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpDailyTelemetryComponent: {
        ...state.blockList.HeatpumpDailyTelemetryComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpDailyTelemetryComponent[instanceId]?.[systemId]?.[deviceId],
              state: 'error' as const,
              error,
            }
          }
        }
      }
    }
  })),
  on(requestSystemHeatpumpEnergyAggregation, (state, { systemId, instanceId, deviceId, period }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpEnergyAggregationComponent: {
        ...state.blockList.HeatpumpEnergyAggregationComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId]?.[systemId]?.[deviceId],
              [period === 'month' ? 'monthlyState' : 'yearlyState']: 'pending' as const,
            }
          }
        }
      }
    }
  })),
  on(updateSystemHeatpumpEnergyAggregation, (state, { systemId, instanceId, deviceId, period, aggregationData }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpEnergyAggregationComponent: {
        ...state.blockList.HeatpumpEnergyAggregationComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId]?.[systemId]?.[deviceId],
              [period === 'month' ? 'monthlyData' : 'yearlyData']: aggregationData,
              [period === 'month' ? 'monthlyState' : 'yearlyState']: 'ready' as const,
            }
          }
        }
      }
    }
  })),
  on(setSystemHeatpumpEnergyAggregationError, (state, { systemId, instanceId, deviceId, period, error }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      HeatpumpEnergyAggregationComponent: {
        ...state.blockList.HeatpumpEnergyAggregationComponent,
        [instanceId]: {
          ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId],
          [systemId]: {
            ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId]?.[systemId],
            [deviceId]: {
              ...state.blockList.HeatpumpEnergyAggregationComponent[instanceId]?.[systemId]?.[deviceId],
              [period === 'month' ? 'monthlyState' : 'yearlyState']: 'error' as const,
              error,
            }
          }
        }
      }
    }
  })),
  on(realTimeTelemetryActivated, (state, { systemId, startTS, duration }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      RealTimeTelemetryComponent: {
        ...state.blockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...state.blockList.RealTimeTelemetryComponent?.[systemId],
          active: true,
          duration,
          endedAt: undefined,
          lastTelemetryReceivedAt: undefined,
          lastTelemetryTs: undefined,
          overlayHidden: false,
          overlayRequestedByUser: state.blockList.RealTimeTelemetryComponent?.[systemId]?.overlayRequestedByUser ?? false,
          startTS: normalizeRTTimestamp(startTS) ?? startTS,
          stopping: false,
          telemetryCount: 0,
        },
      },
    },
  })),
  on(deactivateRealTimeTelemetry, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      RealTimeTelemetryComponent: {
        ...state.blockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...state.blockList.RealTimeTelemetryComponent?.[systemId],
          stopping: true,
        },
      },
    },
  })),
  on(realTimeTelemetryActivationFailed, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      RealTimeTelemetryComponent: {
        ...state.blockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...state.blockList.RealTimeTelemetryComponent?.[systemId],
          active: false,
          stopping: false,
        },
      },
    },
  })),
  on(realTimeTelemetryDeactivationFailed, (state, { systemId }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      RealTimeTelemetryComponent: {
        ...state.blockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...state.blockList.RealTimeTelemetryComponent?.[systemId],
          stopping: false,
        },
      },
    },
  })),
  on(realTimeTelemetryDataReceived, (state, { systemId, data }) => {
    const rtInverter = RealTimeTelemetryService.mapToInverterForPowerFlow(data);
    const rtPowerState = BuildPowerFlow(rtInverter);
    const rtEnergyState = RealTimeTelemetryService.mapToTotalizerEnergy(data);
    const rtDailyEnergy = RealTimeTelemetryService.mapToDailyEnergy(data);
    const rtDailyPowerPoint = RealTimeTelemetryService.mapToDailyPowerPoint(data);

    let newBlockList = { ...state.blockList };

    // Update RealTimeTelemetryComponent: keep RT session UI metadata in store.
    const currentRT = newBlockList.RealTimeTelemetryComponent?.[systemId];
    newBlockList = {
      ...newBlockList,
      RealTimeTelemetryComponent: {
        ...newBlockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...currentRT,
          active: true,
          lastTelemetryReceivedAt: Date.now(),
          lastTelemetryTs: data.TS,
          stopping: false,
          telemetryCount: (currentRT?.telemetryCount ?? 0) + 1,
        },
      },
    };

    // Update FlowChartComponent: all instances for this systemId
    const updatedFlowChart = { ...newBlockList.FlowChartComponent };
    for (const instanceId of Object.keys(updatedFlowChart)) {
      if (updatedFlowChart[instanceId]?.[systemId]) {
        updatedFlowChart[instanceId] = {
          ...updatedFlowChart[instanceId],
          [systemId]: {
            systemPowerState: rtPowerState,
            state: 'ready' as const,
          },
        };
      }
    }
    newBlockList = { ...newBlockList, FlowChartComponent: updatedFlowChart };

    // Update SystemTotalizerComponent: all instances for this systemId
    const updatedTotalizer = { ...newBlockList.SystemTotalizerComponent };
    for (const instanceId of Object.keys(updatedTotalizer)) {
      if (updatedTotalizer[instanceId]?.[systemId]) {
        updatedTotalizer[instanceId] = {
          ...updatedTotalizer[instanceId],
          [systemId]: {
            systemEnergyState: rtEnergyState,
            state: 'ready' as const,
          },
        };
      }
    }
    newBlockList = { ...newBlockList, SystemTotalizerComponent: updatedTotalizer };

    // Update DailyStateOverviewComponent: energies + append power point
    const updatedDaily = { ...newBlockList.DailyStateOverviewComponent };
    for (const instanceId of Object.keys(updatedDaily)) {
      if (updatedDaily[instanceId]?.[systemId]) {
        const existing = updatedDaily[instanceId][systemId];
        const existingPowers = existing?.systemPowerState ?? [];
        const existingRtPoints = existing?.rtPowerPoints ?? [];
        updatedDaily[instanceId] = {
          ...updatedDaily[instanceId],
          [systemId]: {
            ...existing,
            systemEnergyState: rtDailyEnergy,
            systemPowerState: [...existingPowers, rtDailyPowerPoint],
            rtPowerPoints: [...existingRtPoints, rtDailyPowerPoint],
            energyRequestState: 'ready' as const,
            powerRequestState: 'ready' as const,
          },
        };
      }
    }
    newBlockList = { ...newBlockList, DailyStateOverviewComponent: updatedDaily };

    // Update EnergyDataOverviewComponent: only if mode is 'day'
    const updatedEnergyOverview = { ...newBlockList.EnergyDataOverviewComponent };
    for (const instanceId of Object.keys(updatedEnergyOverview)) {
      if (updatedEnergyOverview[instanceId]?.[systemId]) {
        const existing = updatedEnergyOverview[instanceId][systemId];
        if (existing?.mode === 'day') {
          updatedEnergyOverview[instanceId] = {
            ...updatedEnergyOverview[instanceId],
            [systemId]: {
              ...existing,
              systemEnergyState: [rtDailyEnergy],
              state: 'ready' as const,
            },
          };
        }
      }
    }
    newBlockList = { ...newBlockList, EnergyDataOverviewComponent: updatedEnergyOverview };

    // Update MonthlyYearlyEnergyOverviewComponent: patch current day energies in existing dataset
    const updatedMonthlyYearly = { ...newBlockList.MonthlyYearlyEnergyOverviewComponent };
    const rtDate = new Date(data.TS * 1000).toISOString().slice(0, 10);
    for (const instanceId of Object.keys(updatedMonthlyYearly)) {
      if (updatedMonthlyYearly[instanceId]?.[systemId]) {
        const existing = updatedMonthlyYearly[instanceId][systemId];
        const currentEnergies = existing?.systemEnergyState ?? [];
        let hasPatchedDay = false;
        const nextEnergies = currentEnergies.map((entry: any) => {
          const entryDate = entry?.SK ? parseSKonAggregateDailies(entry.SK) : undefined;
          if (entryDate?.slice(0, 10) !== rtDate) {
            return entry;
          }
          hasPatchedDay = true;
          return {
            ...entry,
            EG1: rtDailyEnergy.EG1,
            EG2: rtDailyEnergy.EG2,
            EG3: rtDailyEnergy.EG3,
            EG4: rtDailyEnergy.EG4,
            EG5: rtDailyEnergy.EG5,
            EG6: rtDailyEnergy.EG6,
            TS: data.TS,
          };
        });

        if (hasPatchedDay) {
          updatedMonthlyYearly[instanceId] = {
            ...updatedMonthlyYearly[instanceId],
            [systemId]: {
              ...existing,
              systemEnergyState: nextEnergies,
              state: 'ready' as const,
            },
          };
        }
      }
    }
    newBlockList = { ...newBlockList, MonthlyYearlyEnergyOverviewComponent: updatedMonthlyYearly };

    // Update PlantCardComponent: mark as connected
    const updatedPlantCard = { ...newBlockList.PlantCardComponent };
    if (updatedPlantCard[systemId]) {
      updatedPlantCard[systemId] = {
        ...updatedPlantCard[systemId],
        plantStatus: 'connectedAndUpdated',
        state: 'ready' as const,
        timestamp: Date.now(),
      };
    }
    newBlockList = { ...newBlockList, PlantCardComponent: updatedPlantCard };

    return {
      ...state,
      blockList: newBlockList,
    };
  }),
  on(realTimeTelemetrySessionEnded, (state, { systemId, endedAt }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      RealTimeTelemetryComponent: {
        ...state.blockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...state.blockList.RealTimeTelemetryComponent?.[systemId],
          active: false,
          endedAt,
          overlayHidden: false,
          overlayRequestedByUser: false,
          stopping: false,
        },
      },
    },
  })),
  on(setRealTimeTelemetryOverlayHidden, (state, { systemId, hidden }) => ({
    ...state,
    blockList: {
      ...state.blockList,
      RealTimeTelemetryComponent: {
        ...state.blockList.RealTimeTelemetryComponent,
        [systemId]: {
          ...state.blockList.RealTimeTelemetryComponent?.[systemId],
          overlayHidden: hidden,
          // Mark local user intent when explicitly opening the overlay from console/start.
          overlayRequestedByUser: hidden
            ? state.blockList.RealTimeTelemetryComponent?.[systemId]?.overlayRequestedByUser ?? false
            : true,
        },
      },
    },
  })),
);

export function blockReducer(state: BlockState | undefined, action: Action) {
  return _blockReducer(state, action);
}
